/** @deprecated Use getCategoryIcon with parent category context instead. */
export const TYPE_SUBCATEGORY_ICON = '🏷️';

const ICON_MAP_BY_NAME: Record<string, string> = {
  Electronics: '📱',
  'Electronics & Advanced Technology': '📱',
  Fashion: '👗',
  'Fashion, Beauty & Personal Care': '👗',
  'Home & Garden': '🛋️',
  'Home, Garden & Infrastructure': '🛋️',
  'Sports & Outdoors': '⚽',
  'Sports, Hobbies & Leisure': '⚽',
  'Books & Media': '📚',
  'Education & Specialized Training': '📚',
  'Health & Beauty': '💄',
  'Toys & Games': '🧸',
  Automotive: '🚗',
  Groceries: '🛒',
  'Jewelry & Watches': '💍',
  'Pet Supplies': '🐾',
  'Office Supplies': '📎',
  Others: '📦',
  Vehicle: '🚗',
  Vehicles: '🚗',
  'Vehicles & Sustainable Transport': '🚗',
  Property: '🏠',
  'Real Estate': '🏠',
  'Real Estate & Property': '🏠',
  'Events, Parties & Seasonal': '🎄',
  'Professional Jobs & Careers': '💼',
  'Business, Industry & Energy': '🏭',
  'Specialized Services': '🔧',
  'Family, Kids & Community': '👶',
  'Hobby & Sports': '⚽',
  Jobs: '💼',
  Business: '🏭',
  Entertainment: '🎭',
  Education: '🎓',
  Services: '🔧',
  'Kids Items': '🧸',
  'Free Stuff': '🎁',
};

const ICON_MAP_BY_ID: Record<string, string> = {
  '1': '📱',
  '2': '👗',
  '3': '🛋️',
  '4': '⚽',
  '5': '📚',
  '6': '💄',
  '7': '🧸',
  '8': '🚗',
  '9': '🛒',
  '10': '💍',
  '11': '🐾',
  '12': '📎',
  '13': '📦',
};

const FONT_AWESOME_ICON_MAP: Record<string, string> = {
  car: '🚗',
  automobile: '🚗',
  truck: '🚗',
  motorcycle: '🏍️',
  home: '🏠',
  house: '🏠',
  building: '🏠',
  mobile: '📱',
  'mobile-alt': '📱',
  laptop: '💻',
  tv: '📺',
  desktop: '🖥️',
  couch: '🛋️',
  tshirt: '👗',
  'user-tie': '💼',
  briefcase: '💼',
  industry: '🏭',
  book: '📚',
  wrench: '🔧',
  tools: '🔧',
  baby: '👶',
  football: '⚽',
  'shopping-cart': '🛒',
  gem: '💍',
  paw: '🐾',
  box: '📦',
};

function isFontAwesomeClass(icon: string): boolean {
  return /^\s*(fas|fab|far|fal|fa)\s+fa-|^\s*fa-/.test(icon.trim());
}

function iconFromFontAwesome(dbIcon: string): string | undefined {
  const token = dbIcon
    .trim()
    .toLowerCase()
    .replace(/^(fas|fab|far|fal|fa)\s+fa-/, '')
    .replace(/^fa-/, '');
  return FONT_AWESOME_ICON_MAP[token];
}

/** Keyword hints for subcategory names (e.g. "Cars", "Laptops"). */
function iconFromSubcategoryName(name: string): string | undefined {
  const n = name.toLowerCase();
  if (/\b(car|van|motor|motorbike|truck|bus|vehicle|tyre|auto|ev|hybrid|scooter|bicycle|boat|marine)\b/.test(n)) {
    return '🚗';
  }
  if (/\b(house|apartment|villa|property|land|rental|office|warehouse|commercial)\b/.test(n)) {
    return '🏠';
  }
  if (/\b(laptop|phone|tablet|computer|pc|mobile|tv|monitor|electronic|console|gaming|smartwatch|camera)\b/.test(n)) {
    return '📱';
  }
  if (/\b(furniture|garden|kitchen|decor|lighting|bathroom|paint|tool)\b/.test(n)) {
    return '🛋️';
  }
  if (/\b(clothing|fashion|jewel|watch|bag|cosmetic|skincare|fragrance|hair|grooming)\b/.test(n)) {
    return '👗';
  }
  if (/\b(party|christmas|halloween|gift|hamper|seasonal|festival)\b/.test(n)) {
    return '🎄';
  }
  if (/\b(sport|fitness|gym|music|book|game|travel|camp|hike|photo)\b/.test(n)) {
    return '⚽';
  }
  if (/\b(job|career|internship|hr|finance|legal|marketing)\b/.test(n)) {
    return '💼';
  }
  if (/\b(solar|generator|industrial|machinery|wholesale|packaging|energy)\b/.test(n)) {
    return '🏭';
  }
  if (/\b(education|school|textbook|tutor|course|training)\b/.test(n)) {
    return '📚';
  }
  if (/\b(service|plumbing|electrical|cleaning|moving|construction|renovation)\b/.test(n)) {
    return '🔧';
  }
  if (/\b(baby|child|kid|pet|community|donation|free)\b/.test(n)) {
    return '👶';
  }
  return undefined;
}

function resolveIconByName(name: string): string | undefined {
  const normalized = name.trim();
  if (!normalized) return undefined;
  return ICON_MAP_BY_NAME[normalized] ?? iconFromSubcategoryName(normalized);
}

/** Category icon mapping – same as buyme-backend utils/categoryIcons */
export function getCategoryIcon(
  categoryId: string | number,
  categoryName?: string,
  dbIcon?: string | null,
  parentCategoryName?: string | null,
): string {
  const normalizedName = String(categoryName ?? '').trim();
  const normalizedParent = String(parentCategoryName ?? '').trim();

  const isFa = typeof dbIcon === 'string' && isFontAwesomeClass(dbIcon);
  const safeDbIcon = dbIcon && !isFa ? dbIcon : undefined;
  const faIcon = typeof dbIcon === 'string' && isFa ? iconFromFontAwesome(dbIcon) : undefined;

  const direct =
    (normalizedName ? resolveIconByName(normalizedName) : undefined) ||
    iconMapByIdFallback(categoryId) ||
    faIcon ||
    safeDbIcon;

  if (direct) return direct;

  if (normalizedParent) {
    const parentIcon = resolveIconByName(normalizedParent);
    if (parentIcon) return parentIcon;
  }

  return '📦';
}

function iconMapByIdFallback(categoryId: string | number): string | undefined {
  return ICON_MAP_BY_ID[String(categoryId)];
}
